import { router, usePage } from '@inertiajs/react';
import { useEffect, useState } from 'react';

import {
  GLOBAL_VALIDATION_ERROR_EVENT,
  type GlobalValidationErrorDetail
} from '@/constants/globalValidationError';
import { globalValidationErrorMessage } from '@/lib/globalValidationErrorDispatch';

import { Modal } from './Modal';

/**
 * Global surface for writes rejected with a validation message that belongs to no form field — a
 * lifecycle hook workflow refusing the write, and any future rejection listed in
 * `MODAL_VALIDATION_ERROR_KEYS`. Nothing else renders those keys: a record form would label the
 * message with its raw key, and the JSON API writes behind the pages runtime have no error surface
 * at all.
 *
 * Covers all three transports: Inertia router visits ({@link usePage} props and the router's
 * `error` event) and plain HTTP calls — axios and Inertia's `useHttp` — via
 * {@link GLOBAL_VALIDATION_ERROR_EVENT}. Mounted wherever the other global error modals are.
 */
export function GlobalValidationErrorModal() {
  const { errors } = usePage().props;
  const [message, setMessage] = useState<string | null>(null);

  // Adjusting during render (rather than in an effect) avoids a paint with the previous message.
  // The sentinel initializer makes the first render evaluate the current `errors`.
  const [prevErrors, setPrevErrors] = useState<typeof errors | undefined>(undefined);
  if (errors !== prevErrors) {
    setPrevErrors(errors);
    setMessage(globalValidationErrorMessage(errors));
  }

  // Inertia may leave `errors` referentially stable when a second visit fails with the same
  // payload, so the check above does not re-run. The router's `error` event fires on every 422.
  useEffect(() => {
    return router.on('error', (event) => {
      const next = globalValidationErrorMessage(event.detail.errors);

      if (next !== null) {
        setMessage(next);
      }
    });
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<GlobalValidationErrorDetail>).detail;

      if (detail?.message) {
        setMessage(detail.message);
      }
    };

    window.addEventListener(GLOBAL_VALIDATION_ERROR_EVENT, handler);

    return () => window.removeEventListener(GLOBAL_VALIDATION_ERROR_EVENT, handler);
  }, []);

  const handleClose = () => {
    setMessage(null);
  };

  return (
    <Modal
      show={message !== null}
      onClose={handleClose}
      title="Cannot Complete Action"
      maxWidth="md"
      closeable={false}
      cancelLabel=""
      submitLabel="OK"
      onSubmit={(e) => {
        e.preventDefault();
        handleClose();
      }}
    >
      <p className="whitespace-pre-wrap text-foreground">{message}</p>
    </Modal>
  );
}
